import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, fmt, fmtDelta, CATEGORIES } from "../../lib/game";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Loader2, Plus, Users, Copy, ChevronLeft, Trophy, Clock, Crown, BarChart3, Download, Sparkles,
  Megaphone, LineChart,
} from "lucide-react";

const CodePill = ({ code }) => (
  <button
    type="button"
    data-testid="group-code-pill"
    onClick={() => { navigator.clipboard?.writeText(code); toast.success("Join code copied"); }}
    className="inline-flex items-center gap-2 font-mono font-bold text-lg tracking-[0.2em] px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
  >
    {code} <Copy className="w-3.5 h-3.5" />
  </button>
);

const GroupDetail = ({ groupId, onBack }) => {
  const [data, setData] = useState(null);
  const [hl, setHl] = useState(null);
  const [genBusy, setGenBusy] = useState(false);

  const load = () => api.get(`/groups/${groupId}`).then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { setHl(null); load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [groupId]);

  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>;
  if (data === false) return <p className="text-center text-slate-400 py-20">Couldn't load this group.</p>;

  const { group, players, stats, insights } = data;
  const ranked = [...players].sort((a, b) => b.net_worth - a.net_worth);
  const displayHl = hl || group.highlights;

  const exportCsv = async () => {
    try {
      const res = await api.get(`/groups/${groupId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${group.name}-results.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Couldn't export CSV");
    }
  };

  const generateHighlights = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/highlights`);
      setHl(data);
      toast.success("Read-aloud highlights ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate highlights");
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div>
      <button onClick={onBack} data-testid="group-back-btn" className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-4">
        <ChevronLeft className="w-4 h-4" /> All groups
      </button>

      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-50">{group.name}</h2>
            <p className="text-sm text-slate-400 mt-0.5">Share this code so players can join your group.</p>
          </div>
          <div className="flex items-center gap-2">
            <CodePill code={group.join_code} />
            <Button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              variant="outline"
              className="border-slate-700 bg-slate-950/50 text-slate-100 hover:bg-slate-800 h-10"
            >
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[
            { l: "Players", v: stats.total_players, Icon: Users },
            { l: "Completed", v: stats.completed, Icon: Trophy },
            { l: "In Progress", v: stats.in_progress, Icon: Clock },
            { l: "Avg Net Worth", v: fmt(stats.avg_net_worth), Icon: BarChart3 },
          ].map(({ l, v, Icon }) => (
            <div key={l} className="rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2.5">
              <Icon className="w-4 h-4 text-emerald-400 mb-1" />
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{l}</p>
              <p className="font-mono text-lg font-bold text-slate-100">{v}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.06] to-transparent bg-slate-900/60 p-6 mb-5" data-testid="insight-highlights">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-emerald-400" />
            <h3 className="font-heading text-lg font-bold text-slate-100">Read-Aloud Highlights</h3>
          </div>
          <Button
            data-testid="generate-highlights-btn"
            onClick={generateHighlights}
            disabled={genBusy || stats.total_players === 0}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold h-9"
          >
            {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {genBusy ? "Writing…" : displayHl ? "Regenerate" : "Generate"}
          </Button>
        </div>
        {displayHl ? (
          <>
            <p className="text-[15px] leading-relaxed text-slate-200" data-testid="highlights-text">{displayHl.text}</p>
            <p className="text-xs text-slate-500 mt-2">A warm summary you can read aloud to your group.</p>
          </>
        ) : (
          <p className="text-sm text-slate-400">
            Generate a warm, plain-language summary you can read aloud to your group once players have
            made some decisions.
          </p>
        )}
      </div>

      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 mb-5" data-testid="cohort-insights">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <h3 className="font-heading text-lg font-bold text-slate-100">Cohort Insights</h3>
        </div>
        {(!insights || insights.total_decisions === 0) ? (
          <p className="text-sm text-slate-400">Insights appear here as your players make decisions.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Where the group spent its rounds</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(insights.category_counts).sort((a, b) => b[1] - a[1]).map(([cat, n]) => {
                  const m = CATEGORIES[cat] || CATEGORIES.SAVE;
                  return (
                    <span key={cat} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${m.badge}`}>
                      {m.label}: {n}
                    </span>
                  );
                })}
              </div>
            </div>
            {insights.top_concepts?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Most engaged financial concepts</p>
                <div className="flex flex-wrap gap-2">
                  {insights.top_concepts.map(([tag, n]) => (
                    <span key={tag} className="text-xs font-mono px-2.5 py-1 rounded-full border border-slate-700 bg-slate-950/50 text-slate-300">
                      {tag} · {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {insights.persona_counts?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Coach personas across the cohort</p>
                <div className="flex flex-wrap gap-2">
                  {insights.persona_counts.map(([persona, n]) => (
                    <span key={persona} className="text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                      {persona}{n > 1 ? ` ×${n}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Leaderboard</h3>
      {ranked.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-slate-800 bg-slate-900/50 text-slate-400">
          No players have joined yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="group-players-list">
          {ranked.map((p, i) => {
            const complete = p.status === "complete";
            const change = p.net_worth - (p.starting_net_worth ?? 0);
            return (
              <div key={p.session_id} className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className={`w-7 text-center font-mono font-bold ${i === 0 && complete ? "text-yellow-400" : "text-slate-500"}`}>
                    {i === 0 && complete ? <Crown className="w-4 h-4 mx-auto" /> : i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-100 truncate">{p.nickname}</p>
                    <p className="text-xs text-slate-400">
                      {complete ? (p.persona || "Completed") : `Round ${p.current_round} of 8`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-slate-100">{fmt(p.net_worth)}</p>
                    {complete && <p className={`text-xs font-mono ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtDelta(change)}</p>}
                  </div>
                </div>
                <div className="mt-2 flex items-center flex-wrap gap-1.5 pl-10" data-testid={`player-coverage-${p.session_id}`}>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Areas explored</span>
                  {["SAVE", "EARN", "INVEST", "SPEND", "LIFE_EVENT"].map((k) => {
                    const m = CATEGORIES[k];
                    const Icon = m.Icon;
                    const n = (p.category_counts || {})[k] || 0;
                    const t = n > 0;
                    return (
                      <span key={k} title={`${m.label}: ${n}`} className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border ${t ? m.badge : "border-slate-800 bg-slate-900/50 text-slate-600"}`}>
                        <Icon className="w-3 h-3" /> {n}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Comparison = ({ onBack }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/me/groups/comparison").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  const chartData = (rows || [])
    .filter((r) => r.player_count > 0)
    .map((r) => ({ name: r.name.length > 12 ? r.name.slice(0, 12) + "…" : r.name, avg: r.avg_net_worth, completed: r.completed, players: r.player_count }));

  return (
    <div data-testid="group-comparison">
      <button onClick={onBack} data-testid="comparison-back-btn" className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-4">
        <ChevronLeft className="w-4 h-4" /> All groups
      </button>
      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex items-center gap-2 mb-1">
          <LineChart className="w-4 h-4 text-emerald-400" />
          <h2 className="font-heading text-2xl font-bold text-slate-50">Group Comparison</h2>
        </div>
        <p className="text-sm text-slate-400 mb-5">Average final net worth across your groups, oldest to newest.</p>

        {rows === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>
        ) : chartData.length === 0 ? (
          <p className="text-slate-400 py-10 text-center">No group activity to compare yet.</p>
        ) : (
          <>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 12, color: "#f1f5f9" }}
                    formatter={(v, n) => [n === "avg" ? fmt(v) : v, n === "avg" ? "Avg net worth" : n]}
                  />
                  <Bar dataKey="avg" fill="#10B981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-5 space-y-2" data-testid="comparison-rows">
              {(rows || []).map((r) => (
                <div key={r.group_id} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-100 truncate">{r.name}</p>
                    <p className="text-xs text-slate-400">{r.completed}/{r.player_count} finished</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-emerald-400">{fmt(r.avg_net_worth)}</p>
                    <p className="text-[11px] text-slate-500">avg net worth</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const Facilitator = () => {
  const [groups, setGroups] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = () => api.get("/me/groups").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  useEffect(() => { if (!selected) load(); }, [selected]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/groups", { name: name.trim() });
      toast.success(`Group "${data.name}" created — code ${data.join_code}`);
      setName("");
      load();
    } catch (e) {
      toast.error("Couldn't create group");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {selected ? (
          <GroupDetail groupId={selected} onBack={() => setSelected(null)} />
        ) : comparing ? (
          <Comparison onBack={() => setComparing(false)} />
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-50">Facilitator Mode</h1>
                <p className="text-slate-400 mt-1 mb-6">
                  Create a group, share the join code, and watch your cohort's progress live.
                </p>
              </div>
              <Button
                data-testid="compare-groups-btn"
                onClick={() => setComparing(true)}
                variant="outline"
                className="border-slate-700 bg-slate-950/50 text-slate-100 hover:bg-slate-800"
              >
                <BarChart3 className="w-4 h-4 mr-1.5" /> Compare Groups
              </Button>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 flex flex-col sm:flex-row gap-3 mb-6">
              <Input
                data-testid="group-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="New group name (e.g. Tuesday Workshop)"
                className="bg-slate-950/60 border-slate-700 text-slate-100"
              />
              <Button
                data-testid="create-group-btn"
                onClick={create}
                disabled={creating || !name.trim()}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold shrink-0"
              >
                <Plus className="w-4 h-4 mr-1" /> Create Group
              </Button>
            </div>

            {groups === null ? (
              <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>
            ) : groups.length === 0 ? (
              <div className="text-center py-16 rounded-3xl border border-slate-800 bg-slate-900/50">
                <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No groups yet. Create one to get a join code.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="groups-list">
                {groups.map((g) => (
                  <motion.button
                    key={g.group_id}
                    type="button"
                    data-testid={`group-item-${g.group_id}`}
                    onClick={() => setSelected(g.group_id)}
                    whileHover={{ scale: 1.01 }}
                    className="w-full text-left rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-slate-600 px-5 py-4 flex items-center gap-4 transition-colors"
                  >
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-100">{g.name}</p>
                      <p className="text-xs text-slate-400">{g.player_count} player{g.player_count === 1 ? "" : "s"}</p>
                    </div>
                    <span className="font-mono font-bold tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-2.5 py-1 text-sm">
                      {g.join_code}
                    </span>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
